import {
    FeatureFlagIdAccessConflicts,
    FeatureFlagIdAccessExplorer,
    FeatureFlagIdAccessRequest,
    FeatureFlagIdAccountProvisioning,
    FeatureFlagIdAiTicketing,
    FeatureFlagIdBillingDashboard,
    FeatureFlagIdCopilotThomas,
    FeatureFlagIdExploreSearchV2,
    FeatureFlagIdExternalTicketing,
    FeatureFlagIdFunctions,
    FeatureFlagIdBranding,
    FeatureFlagIdInventory,
    FeatureFlagIdOauth2Dcr,
    FeatureFlagIdOffboarding,
    FeatureFlagIdPasswordPush,
    FeatureFlagIdStepUpAuth,
    FeatureFlagIdSystemManagement,
    FeatureFlagIdWorkflowsFeatureV2,
    FeatureFlagIdTsdbMetricsDashboards,
    FeatureFlagIdResourceActionGroupUi,
} from 'c1gen/features'
import {
    AccessconflictV1AccessConflictServiceowner,
    AccessreviewV1AccessReviewSearchServiceeditor,
    AccessreviewV1AccessReviewSearchServiceowner,
    AppV1Appseditor,
    AppV1Appsowner,
    AppV1ConnectorServiceeditor,
    AppV1ConnectorServiceowner,
    AutomationsV1AutomationServiceowner,
    FunctionsV1FunctionsServiceowner,
    OffboardingV1OffboardingSearchServiceowner,
    PasswordPushV1PasswordPushFlowServiceowner,
    PolicyV1Policieseditor,
    PolicyV1Policiesowner,
    RequestcatalogV1RequestCatalogManagementServiceeditor,
    RequestcatalogV1RequestCatalogManagementServiceowner,
    Roles,
    TaskV1TaskActionsServiceowner,
    TenantV1Tenantowner,
    TsdbV1TsdbmetricsServiceowner,
    TsdbV1TsdbmetricsServiceviewer,
    UserV1UserSearcheditor,
    UserV1UserSearchowner,
    VaultV1VaultServiceowner,
} from 'c1gen/roles'
import { SystemManagementWarningIndicator } from 'components/pages/admin/settings/system-management/SystemManagementWarningIndicator'
import { NavItemProps } from 'components/pages/sidebar/CollapsingSidebar'
import { DashboardContextRecord } from 'lib/context/app'
import { routes } from 'lib/routes'
import {
    ChartColumn,
    ClipboardCheck,
    Compass,
    ContactRound,
    Home,
    LayoutGrid,
    LogOut,
    Settings,
    ShieldCheck,
    TriangleAlert,
    User,
    Users,
    Waypoints,
    Zap,
} from 'lucide-react'
import { IntlShape } from 'react-intl'

export const getFirstPathSegment = (path: string): string => {
    const parts = path.split('?')[0].split('/').filter(Boolean)

    return parts.length > 0 ? parts[0] : ''
}

export const getAllPathnamesFromNavItems = (
    navItems: NavItemProps[],
    ignoreExtraSegments = false,
): Set<string> => {
    let pathnames: Set<string> = new Set()

    navItems.forEach((item) => {
        if (item.pathname) {
            pathnames.add(
                ignoreExtraSegments
                    ? getFirstPathSegment(item.pathname)
                    : item.pathname,
            )
        }

        if (item.subMenu && item.subMenu.length > 0) {
            pathnames = new Set([
                ...pathnames,
                ...getAllPathnamesFromNavItems(
                    item.subMenu,
                    ignoreExtraSegments,
                ),
            ])
        }
    })

    return pathnames
}

export const getAllRolesFromNavItems = (
    navItems: NavItemProps[],
): Set<Roles> => {
    let roles: Set<Roles> = new Set()

    navItems.forEach((item) => {
        if (item.roles) {
            for (const role of item.roles) {
                roles.add(role)
            }
        }

        if (item.subMenu && item.subMenu.length > 0) {
            roles = new Set([
                ...roles,
                ...getAllRolesFromNavItems(item.subMenu),
            ])
        }
    })

    return roles
}

export const getAdminNavItems = (
    intl: IntlShape,
    dashboardContext: DashboardContextRecord,
): NavItemProps[] => [
    {
        id: 'admin-dashboard',
        icon: Home,
        label: intl.formatMessage({
            id: 'sidebar_admin_dashboard',
            defaultMessage: 'Dashboard',
        }),
        pathname: routes.dashboard,
    },
    {
        id: 'explore',
        icon: Compass,
        label: intl.formatMessage({
            id: 'sidebar_explore',
            defaultMessage: 'Explore',
        }),
        roles: [TenantV1Tenantowner],
        pathname: routes.explore.inventory,
        subMenu: [
            {
                id: 'nav_inventory',
                label: intl.formatMessage({
                    id: 'sidebar_explore_inventory',
                    defaultMessage: 'Inventory',
                }),
                pathname: routes.explore.inventory,
                roles: [TenantV1Tenantowner],
                features: [FeatureFlagIdInventory],
            },
            {
                id: 'nav_search',
                label: intl.formatMessage({
                    id: 'sidebar_explore_query',
                    defaultMessage: 'Search',
                }),
                pathname: routes.explore.search,
                roles: [TenantV1Tenantowner],
                features: [FeatureFlagIdExploreSearchV2],
            },
            {
                id: 'nav_graph',
                label: intl.formatMessage({
                    id: 'sidebar_explore_graph',
                    defaultMessage: 'Graph',
                }),
                pathname: routes.explore.graph,
                roles: [TenantV1Tenantowner],
                features: [FeatureFlagIdAccessExplorer],
            },
        ],
    },
    {
        id: 'admin-analytics',
        icon: ChartColumn,
        label: intl.formatMessage({
            id: 'sidebar_admin_analytics',
            defaultMessage: 'Analytics',
        }),
        pathname: routes.analytics,
        features: [FeatureFlagIdTsdbMetricsDashboards],
        roles: [TsdbV1TsdbmetricsServiceviewer, TsdbV1TsdbmetricsServiceowner],
    },
    {
        id: 'campaigns',
        icon: ClipboardCheck,
        label: intl.formatMessage({
            id: 'sidebar_govern_campaigns',
            defaultMessage: 'Campaigns',
        }),
        pathname: routes.campaigns.list,
        roles: [
            AccessreviewV1AccessReviewSearchServiceeditor,
            AccessreviewV1AccessReviewSearchServiceowner,
        ],
    },
    {
        id: 'manage_applications',
        icon: LayoutGrid,
        label: intl.formatMessage({
            id: 'sidebar_manage_applications',
            defaultMessage: 'Applications',
        }),
        pathname: routes.applications.list,
        roles: [AppV1Appseditor, AppV1Appsowner],
    },
    {
        id: 'access_conflicts',
        icon: TriangleAlert,
        label: intl.formatMessage({
            id: 'sidebar_access_conflicts',
            defaultMessage: 'Access conflicts',
        }),
        pathname: routes.conflictMonitors.list,
        roles: [
            AccessconflictV1AccessConflictServiceowner,
        ],
        count: dashboardContext.get('conflicts'),
        features: [FeatureFlagIdAccessConflicts],
    },
    {
        id: 'manage_access_profiles',
        icon: ContactRound,
        label: intl.formatMessage({
            id: 'sidebar_manage_access_profiles',
            defaultMessage: 'Access profiles',
        }),
        pathname: routes.accessProfiles.list,
        roles: [
            RequestcatalogV1RequestCatalogManagementServiceeditor,
            RequestcatalogV1RequestCatalogManagementServiceowner,
        ],
        features: [FeatureFlagIdAccessRequest],
    },
    {
        id: 'administer_connectors',
        icon: Waypoints,
        label: intl.formatMessage({
            id: 'sidebar_administer_connectors',
            defaultMessage: 'Connectors',
        }),
        pathname: routes.connectors.list,
        roles: [AppV1ConnectorServiceeditor, AppV1ConnectorServiceowner],
    },
    {
        id: 'automations',
        icon: Zap,
        label: intl.formatMessage({
            id: 'sidebar_automations',
            defaultMessage: 'Automations',
        }),
        pathname: routes.automations.list,
        features: [FeatureFlagIdWorkflowsFeatureV2],
        roles: [AutomationsV1AutomationServiceowner],
    },
    {
        id: 'groups',
        icon: Users,
        label: intl.formatMessage({
            id: 'sidebar_groups',
            defaultMessage: 'Groups',
        }),
        pathname: routes.groups.index,
        features: [FeatureFlagIdResourceActionGroupUi],
    },
    {
        id: 'offboarding',
        icon: LogOut,
        label: intl.formatMessage({
            id: 'sidebar_offboarding',
            defaultMessage: 'Offboarding',
        }),
        pathname: routes.offboarding.list,
        roles: [
            OffboardingV1OffboardingSearchServiceowner,
        ],
        features: [FeatureFlagIdOffboarding],
    },
    {
        id: 'manage_tasks',
        icon: ClipboardCheck,
        label: intl.formatMessage({
            id: 'sidebar_manage_tasks',
            defaultMessage: 'Task log',
        }),
        pathname: routes.taskLog,
        roles: [TaskV1TaskActionsServiceowner],
    },
    {
        id: 'directory',
        icon: User,
        label: intl.formatMessage({
            id: 'sidebar_directory',
            defaultMessage: 'Directory',
        }),
        pathname: routes.users.list,
        selectedPaths: Array.from(
            getAllPathnamesFromNavItems(getDirectoryNavItems(intl)),
        ),
        subMenu: getDirectoryNavItems(intl),
    },
    {
        id: 'settings',
        icon: Settings,
        label: intl.formatMessage({
            id: 'sidebar_settings',
            defaultMessage: 'Settings',
        }),
        pathname: routes.settings.root,
        selectedPaths: Array.from(
            getAllPathnamesFromNavItems(getSettingsNavItems(intl)),
        ),
        subMenu: getSettingsNavItems(intl),
        warningIndicator: SystemManagementWarningIndicator,
    },
]

const getDirectoryNavItems = (intl: IntlShape): NavItemProps[] => [
    {
        id: 'manage_users',
        label: intl.formatMessage({
            id: 'sidebar_manage_people',
            defaultMessage: 'Users',
        }),
        pathname: routes.users.list,
        roles: [UserV1UserSearcheditor, UserV1UserSearchowner],
    },
    {
        id: 'dynamic_groups',
        label: intl.formatMessage({
            id: 'sidebar_manage_groups',
            defaultMessage: 'Groups',
        }),
        pathname: routes.directory.groups.list,
        roles: [UserV1UserSearcheditor, UserV1UserSearchowner],
    },
    {
        id: 'user-data-sources',
        label: intl.formatMessage({
            id: 'sidebar_user_data_sources',
            defaultMessage: 'User data sources',
        }),
        pathname: routes.userDataSources.list,
    },
]

const getSettingsNavItems = (intl: IntlShape): NavItemProps[] => [
    {
        id: 'api-keys',
        label: intl.formatMessage({
            id: 'sidebar_api_keys',
            defaultMessage: 'API keys',
        }),
        pathname: routes.settings.apiKeys,
    },
    {
        id: 'agentic-ai',
        label: intl.formatMessage({
            id: 'sidebar_agentic_ai',
            defaultMessage: 'Agentic AI',
        }),
        pathname: routes.settings.agenticAi.list,
        features: [FeatureFlagIdCopilotThomas],
    },
    {
        id: 'tags',
        label: intl.formatMessage({
            id: 'sidebar_tags',
            defaultMessage: 'Attribute values',
        }),
        pathname: routes.settings.tags,
    },
    {
        id: 'ai-connections',
        label: intl.formatMessage({
            id: 'sidebar_ai_connections',
            defaultMessage: 'AI connections',
        }),
        pathname: routes.settings.aiConnections,
        features: [FeatureFlagIdOauth2Dcr],
        // Restrict to Super Admin and Read Only Admin - ReadOnlySuperAdmin is handled
        // by special case in shouldRender, TenantV1Tenantowner is assigned to SuperAdmin
        roles: [TenantV1Tenantowner],
    },
    {
        id: 'billing-and-usage',
        label: intl.formatMessage({
            id: 'sidebar_billing_and_usage',
            defaultMessage: 'Billing and usage',
        }),
        pathname: routes.settings.billingAndUsage,
        features: [FeatureFlagIdBillingDashboard],
    },
    {
        id: 'external-data-sources',
        label: intl.formatMessage({
            id: 'sidebar_external_data_sources',
            defaultMessage: 'External data sources',
        }),
        pathname: routes.settings.externalDataSources,
    },
    {
        id: 'external-ticketing',
        label: intl.formatMessage({
            id: 'sidebar_external_ticketing',
            defaultMessage: 'External ticketing',
        }),
        pathname: routes.settings.externalTicketing.list,
        features: [FeatureFlagIdExternalTicketing],
    },
    {
        id: 'branding',
        label: intl.formatMessage({
            id: 'sidebar_branding',
            defaultMessage: 'Branding',
        }),
        pathname: routes.settings.branding,
        features: [FeatureFlagIdBranding],
    },
    {
        id: 'functions',
        label: intl.formatMessage({
            id: 'sidebar_functions',
            defaultMessage: 'Functions',
        }),
        pathname: routes.settings.functions.list,
        roles: [FunctionsV1FunctionsServiceowner],
        features: [FeatureFlagIdFunctions],
    },
    {
        id: 'notifications',
        label: intl.formatMessage({
            id: 'sidebar_notifications',
            defaultMessage: 'Notifications',
        }),
        pathname: routes.settings.notifications,
    },
    {
        id: 'organization',
        label: intl.formatMessage({
            id: 'sidebar_organization',
            defaultMessage: 'Organization',
        }),
        pathname: routes.settings.organization,
    },
    {
        id: 'passwords',
        label: intl.formatMessage({
            id: 'sidebar_passwords',
            defaultMessage: 'Passwords',
        }),
        pathname: routes.settings.passwords.list,
        features: [FeatureFlagIdPasswordPush],
        roles: [PasswordPushV1PasswordPushFlowServiceowner],
    },
    {
        id: 'govern_policies',
        label: intl.formatMessage({
            id: 'sidebar_policies',
            defaultMessage: 'Policies',
        }),
        pathname: routes.settings.policies.list,
        roles: [PolicyV1Policieseditor, PolicyV1Policiesowner],
    },
    {
        id: 'service-desk-integration',
        label: intl.formatMessage({
            id: 'sidebar_service_desk_integration',
            defaultMessage: 'Service desk integrations',
        }),
        pathname: routes.settings.serviceDeskIntegration.list,
        features: [FeatureFlagIdAiTicketing],
    },
    {
        id: 'sso',
        label: intl.formatMessage({
            id: 'sidebar_sso',
            defaultMessage: 'SSO & sessions',
        }),
        pathname: routes.settings.sso,
    },
    {
        id: 'step-up',
        label: intl.formatMessage({
            id: 'sidebar_step_up',
            defaultMessage: 'Step-up authentication',
        }),
        pathname: routes.settings.stepUp,
        features: [FeatureFlagIdStepUpAuth],
    },
    {
        id: 'system-log',
        label: intl.formatMessage({
            id: 'sidebar_system_log',
            defaultMessage: 'System log',
        }),
        pathname: routes.settings.systemLog,
    },
    {
        id: 'system-management',
        label: intl.formatMessage({
            id: 'sidebar_system_management',
            defaultMessage: 'System management',
        }),
        pathname: routes.settings.systemManagement,
        features: [FeatureFlagIdSystemManagement],
        warningIndicator: SystemManagementWarningIndicator,
    },
    {
        id: 'vaults',
        label: intl.formatMessage({
            id: 'sidebar_vaults',
            defaultMessage: 'Vaults',
        }),
        pathname: routes.settings.vaults.list,
        features: [FeatureFlagIdAccountProvisioning],
        roles: [VaultV1VaultServiceowner],
    },
    {
        id: 'webhooks',
        label: intl.formatMessage({
            id: 'sidebar_webhooks',
            defaultMessage: 'Webhooks',
        }),
        pathname: routes.settings.webhooks,
    },
]

export const getEndUserNavItems = (
    intl: IntlShape,
    dashboardContext: DashboardContextRecord,
): NavItemProps[] => [
    {
        id: 'home',
        label: intl.formatMessage({
            id: 'components_sidebar_dashboard',
            defaultMessage: 'Home',
        }),
        pathname: routes.home,
        isTopLevel: true,
    },
    {
        label: intl.formatMessage({
            id: 'components_sidebar_my_work',
            defaultMessage: 'Requests',
        }),
        pathname: routes.requests.list,
        selectedPaths: [routes.requests.create],
        id: 'assignments',
        isTopLevel: true,
        count:
                (dashboardContext.get('my_requests') || 0) +
                (dashboardContext.get('request_tasks') || 0) +
                (dashboardContext.get('enrollment_tasks') || 0) +
                (dashboardContext.get('revoke_tasks') || 0),
    },
    {
        id: 'access_reviews',
        label: intl.formatMessage({
            id: 'components_sidebar_my_tasks_reviews',
            defaultMessage: 'Access reviews',
        }),
        pathname: routes.assignments.reviews.list,
        selectedPaths: [routes.assignments.reviews.list],
        count: dashboardContext.get('review_tasks'),
        isTopLevel: true,
    },
    {
        id: 'my_groups',
        label: intl.formatMessage({
            id: 'sidebar_groups',
            defaultMessage: 'Groups',
        }),
        pathname: routes.groups.index,
        features: [FeatureFlagIdResourceActionGroupUi],
        isTopLevel: true,
    },
    {
        icon: ShieldCheck,
        label: intl.formatMessage({
            id: 'components_sidebar_my_access',
            defaultMessage: 'Admin',
        }),
        pathname: routes.dashboard,
        id: 'admin',
        isTopLevel: true,
        roles: Array.from(
            getAllRolesFromNavItems(getAdminNavItems(intl, dashboardContext)),
        ),
        selectedPaths: Array.from(
            getAllPathnamesFromNavItems(getAdminNavItems(intl, dashboardContext)),
        ),
    },
]
